import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Avatar,
  Card,
  CardContent,
} from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EuroIcon from '@mui/icons-material/Euro';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import PsychologyIcon from '@mui/icons-material/Psychology';
import FaceIcon from '@mui/icons-material/Face';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import DescriptionIcon from '@mui/icons-material/Description';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import WarningIcon from '@mui/icons-material/Warning';

import { COLORS } from '../ViewPatientDialog.utils';

export default function ReservationTab({ patient }) {
  const [visibleCount, setVisibleCount] = useState(3);

  // État pour la modale Compte Rendu
  const [openCRModal, setOpenCRModal] = useState(false);
  const [selectedCR, setSelectedCR] = useState(null);
  const [selectedReservation, setSelectedReservation] = useState(null);

  // Récupération et tri des réservations
  const reservations = useMemo(() => {
    const list = patient?.client?.reservations || [];
    return [...list].sort((a, b) => {
      const dateA = new Date(a.created_at || a.start_time_reservation || 0);
      const dateB = new Date(b.created_at || b.start_time_reservation || 0);
      return dateB - dateA;
    });
  }, [patient]);

  // Calcul des statistiques en haut
  const stats = useMemo(() => {
    let total = reservations.length;
    let completedWithoutCR = 0;
    let completedWithCR = 0;
    let pendingOrConfirmed = 0;

    reservations.forEach((res) => {
      const lowerStatus = (res.status_reservation || '').toLowerCase();
      const compteRendu = res?.comptes_rendus || res?.compteRendu;
      const hasCR = Boolean(Array.isArray(compteRendu) ? compteRendu.length > 0 : compteRendu);

      if (['completed', 'termine', 'terminé'].includes(lowerStatus)) {
        if (hasCR) {
          completedWithCR++;
        } else {
          completedWithoutCR++;
        }
      } else {
        pendingOrConfirmed++;
      }
    });

    return { total, completedWithoutCR, completedWithCR, pendingOrConfirmed };
  }, [reservations]);

  const displayedReservations = reservations.slice(0, visibleCount);
  const hasMore = visibleCount < reservations.length;

  const handleOpenCRModal = (cr, reservation) => {
    const crData = Array.isArray(cr) ? cr[0] : cr;
    setSelectedCR(crData);
    setSelectedReservation(reservation);
    setOpenCRModal(true);
  };

  const handleCloseCRModal = () => {
    setOpenCRModal(false);
    setSelectedCR(null);
    setSelectedReservation(null);
  };

  // Impression / Téléchargement PDF du Compte Rendu
  const handlePrintPDF = () => {
    const printElement = document.getElementById('printable-compte-rendu-tab');
    if (!printElement) return;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(`
      <html>
        <head>
          <title>Compte Rendu - Patient ${patient?.prenom_patient || ''} ${patient?.nom_patient || ''}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; color: #2B3674; margin: 0; }
            .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #4318FF; padding-bottom: 10px; margin-bottom: 20px; }
            .title { font-size: 20px; font-weight: bold; color: #4318FF; }
            .subtitle { font-size: 15px; font-weight: bold; color: #2B3674; margin-top: 4px; }
            .badge { background: #E6F9F5; color: #05CD99; padding: 6px 12px; border-radius: 12px; font-size: 12px; font-weight: bold; border: 1px solid #B2F1E4; }
            .card { background: #F4F7FE; padding: 15px; border-radius: 12px; margin-bottom: 20px; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; font-size: 13px; }
            .section { padding: 15px; border-radius: 8px; margin-bottom: 15px; }
            .sec-1 { background: #F8FAFC; border-left: 4px solid #0284C7; }
            .sec-2 { background: #F0F9FF; border-left: 4px solid #0EA5E9; }
            .sec-3 { background: #F0FDF4; border-left: 4px solid #10B981; }
            .sec-4 { background: #EEF2FF; border-left: 4px solid #6366F1; }
            .sec-title { font-weight: bold; margin-bottom: 6px; font-size: 13px; display: flex; align-items: center; gap: 8px; }
            
            svg {
              width: 18px !important;
              height: 18px !important;
              max-width: 18px !important;
              max-height: 18px !important;
              display: inline-block !important;
              vertical-align: middle !important;
            }

            .footer { margin-top: 30px; text-align: center; font-size: 11px; color: #A3AED0; border-top: 1px solid #E2E8F0; padding-top: 10px; }
          </style>
        </head>
        <body>
          ${printElement.innerHTML}
          <div class="footer">Plateforme S.O.A • Document officiel généré le ${new Date().toLocaleDateString('fr-FR')}</div>
        </body>
      </html>
    `);
    doc.close();

    iframe.contentWindow.focus();
    setTimeout(() => {
      iframe.contentWindow.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    }, 200);
  };

  const getStatusChip = (status) => {
    let bg = '#FEF3C7';
    let color = '#D97706';
    let label = status || 'Inconnu';
    const lowerStatus = (status || '').toLowerCase();

    if (lowerStatus === 'pending') {
      bg = '#FEF3C7';
      color = '#D97706';
      label = 'En attente';
    } else if (['approved', 'validated', 'valider', 'confirmed', 'confirme', 'confirmé'].includes(lowerStatus)) {
      bg = '#D1FAE5';
      color = '#059669';
      label = 'Confirmé';
    } else if (['cancelled', 'annule', 'annulé'].includes(lowerStatus)) {
      bg = '#FEE2E2';
      color = '#DC2626';
      label = 'Annulé';
    } else if (['completed', 'termine', 'terminé'].includes(lowerStatus)) {
      bg = '#DBEAFE';
      color = '#1D4ED8';
      label = 'Terminé';
    }

    return (
      <Chip
        label={label}
        size="small"
        sx={{
          backgroundColor: bg,
          color: color,
          fontWeight: 700,
          textTransform: 'capitalize',
        }}
      />
    );
  };

  return (
    <Box>
      {/* 📊 CARTES DE STATISTIQUES EN HAUT */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <Card sx={{ flex: 1, borderRadius: '16px', boxShadow: '0px 4px 12px rgba(0,0,0,0.03)', border: '1px solid #ECEEF0' }}>
          <CardContent sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="caption" sx={{ color: '#A3AED0', fontWeight: 700, textTransform: 'uppercase', fontSize: '11px' }}>
                  Total Réservations
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#2B3674', mt: 0.5 }}>
                  {stats.total}
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: 'rgba(67, 24, 255, 0.1)', color: '#4318FF', width: 38, height: 38 }}>
                <EqualizerIcon fontSize="small" />
              </Avatar>
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1, borderRadius: '16px', boxShadow: '0px 4px 12px rgba(0,0,0,0.03)', border: '1px solid #B2F1E4' }}>
          <CardContent sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="caption" sx={{ color: '#05CD99', fontWeight: 700, textTransform: 'uppercase', fontSize: '11px' }}>
                  Terminées (CR Rédigé)
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#05CD99', mt: 0.5 }}>
                  {stats.completedWithCR}
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: '#E6F9F5', color: '#05CD99', width: 38, height: 38 }}>
                <CheckCircleIcon fontSize="small" />
              </Avatar>
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1, borderRadius: '16px', boxShadow: '0px 4px 12px rgba(0,0,0,0.03)', border: '1px solid #FDCFCB' }}>
          <CardContent sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="caption" sx={{ color: '#EE5D50', fontWeight: 700, textTransform: 'uppercase', fontSize: '11px' }}>
                  Terminées (CR Manquant)
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#EE5D50', mt: 0.5 }}>
                  {stats.completedWithoutCR}
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: '#FEEFEE', color: '#EE5D50', width: 38, height: 38 }}>
                <WarningIcon fontSize="small" />
              </Avatar>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: '16px',
          backgroundColor: '#FFFFFF',
          border: '1px solid #E2E8F0',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <EventAvailableIcon sx={{ color: COLORS.primary }} />
          <Typography variant="h6" sx={{ fontWeight: 800, color: COLORS.text }}>
            Réservations du patient ({reservations.length})
          </Typography>
        </Stack>
        <Typography variant="body2" sx={{ color: COLORS.muted, mb: 3 }}>
          Suivi des rendez-vous, validation et comptes rendus pour {patient?.prenom_patient || 'ce patient'}.
        </Typography>

        {reservations.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: COLORS.muted, fontStyle: 'italic' }}>
              Aucune réservation enregistrée pour ce patient.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={3}>
            {displayedReservations.map((res) => {
              const offreInfo = res?.offre?.info_offre;
              const userObj = res?.user;
              const compteRendu = res?.comptes_rendus || res?.compteRendu;

              const reservationId = res?.id_offre_reservation || res?.id || '—';
              const reservationCode = res?.code_reservation;

              const nomCompletUser = userObj
                ? [userObj.prenom_user, userObj.nom_user].filter(Boolean).join(' ')
                : 'Non assigné';

              const roleLabelUser = userObj?.role_label
                ? userObj.role_label.charAt(0).toUpperCase() + userObj.role_label.slice(1)
                : 'Intervenant';

              const lowerStatus = (res.status_reservation || '').toLowerCase();
              
              const hasCompteRendu = Boolean(
                Array.isArray(compteRendu) ? compteRendu.length > 0 : compteRendu
              );

              let progressScore = 25;
              let statusLabelText = 'En attente de validation (25%)';

              if (['completed', 'termine', 'terminé'].includes(lowerStatus)) {
                if (hasCompteRendu) {
                  progressScore = 100;
                  statusLabelText = 'Terminé et compte rendu fait (100%)';
                } else {
                  progressScore = 90;
                  statusLabelText = 'Terminé - Compte rendu manquant (90%)';
                }
              } else if (['approved', 'validated', 'valider', 'confirmed', 'confirme', 'confirmé'].includes(lowerStatus)) {
                progressScore = 50;
                statusLabelText = 'Confirmé (50%)';
              }

              return (
                <Paper
                  key={reservationId}
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: '12px',
                    border: '1px solid #E2E8F0',
                    backgroundColor: '#F8FAFC',
                    transition: 'all 0.2s ease',
                    '&:hover': { borderColor: COLORS.primary, backgroundColor: '#FFFFFF' },
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={1.5}
                    sx={{ mb: 1 }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: COLORS.text }}>
                        {offreInfo?.titre_offre || 'Offre sans titre'}
                      </Typography>

                      <Chip
                        icon={<ConfirmationNumberIcon sx={{ fontSize: '14px !important' }} />}
                        label={`Réservation #${reservationId}${reservationCode ? ` (${reservationCode})` : ''}`}
                        size="small"
                        sx={{
                          backgroundColor: '#EEF2FF',
                          color: COLORS.primary,
                          fontWeight: 700,
                          fontSize: '0.75rem',
                        }}
                      />
                    </Stack>

                    {getStatusChip(res.status_reservation)}
                  </Stack>

                  {res.created_at && (
                    <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 1.5 }}>
                      <CalendarTodayIcon sx={{ fontSize: 13, color: COLORS.muted }} />
                      <Typography variant="caption" sx={{ color: COLORS.muted, fontWeight: 600 }}>
                        Créée le : {new Date(res.created_at).toLocaleString('fr-FR')}
                      </Typography>
                    </Stack>
                  )}

                  <Box sx={{ mb: 2, p: 1.5, borderRadius: '8px', backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: COLORS.text }}>
                        {statusLabelText}
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: progressScore === 100 ? '#059669' : COLORS.primary }}>
                        {progressScore}%
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={progressScore}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: '#E2E8F0',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: progressScore === 100 ? '#059669' : COLORS.primary,
                        },
                      }}
                    />
                    <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {progressScore >= 50 ? (
                          <CheckCircleIcon sx={{ fontSize: 14, color: '#059669' }} />
                        ) : (
                          <PendingIcon sx={{ fontSize: 14, color: '#D97706' }} />
                        )}
                        <Typography variant="caption" sx={{ color: progressScore >= 50 ? '#059669' : '#D97706', fontWeight: 600 }}>
                          {progressScore >= 50 ? 'Validé / Confirmé' : 'En attente'}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {hasCompteRendu ? (
                          <CheckCircleIcon sx={{ fontSize: 14, color: '#059669' }} />
                        ) : (
                          <PendingIcon sx={{ fontSize: 14, color: '#D97706' }} />
                        )}
                        <Typography variant="caption" sx={{ color: hasCompteRendu ? '#059669' : '#D97706', fontWeight: 600 }}>
                          {hasCompteRendu ? 'Compte rendu fait' : 'Compte rendu manquant'}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>

                  <Typography variant="body2" sx={{ color: COLORS.muted, mb: 2 }}>
                    {offreInfo?.description_offre || 'Aucune description fournie.'}
                  </Typography>

                  <Divider sx={{ my: 1.5 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <SupervisorAccountIcon sx={{ fontSize: 16, color: COLORS.primary }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, color: COLORS.text }}>
                          {roleLabelUser} : {nomCompletUser}
                        </Typography>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LocationOnIcon sx={{ fontSize: 16, color: COLORS.primary }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, color: COLORS.text }}>
                          {offreInfo?.city_ws ? `${offreInfo.city_ws} (${offreInfo.country_ws})` : 'Lieu non spécifié'}
                        </Typography>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <AccessTimeIcon sx={{ fontSize: 16, color: COLORS.primary, mt: 0.3 }} />
                        <Box>
                          <Typography variant="caption" display="block" sx={{ fontWeight: 700, color: COLORS.text }}>
                            Début : {res.start_time_reservation ? new Date(res.start_time_reservation).toLocaleString('fr-FR') : '—'}
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ fontWeight: 700, color: COLORS.muted }}>
                            Fin : {res.end_time_reservation ? new Date(res.end_time_reservation).toLocaleString('fr-FR') : '—'}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <EuroIcon sx={{ fontSize: 16, color: COLORS.primary }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, color: COLORS.text }}>
                          Prix standard : {offreInfo?.prix_offre ? `${offreInfo.prix_offre} €` : 'Gratuit'}{' '}
                          {offreInfo?.prix_special_soa && ` (Spécial SOA : ${offreInfo.prix_special_soa} €)`}
                        </Typography>
                      </Stack>
                    </Grid>
                  </Grid>

                  {/* Bouton pour ouvrir la modale du compte rendu */}
                  {hasCompteRendu && (
                    <>
                      <Divider sx={{ my: 2, borderStyle: 'dashed' }} />
                      <Box sx={{ p: 2, borderRadius: '8px', backgroundColor: '#F1F5F9', border: '1px solid #CBD5E1' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <AssignmentIcon sx={{ fontSize: 18, color: COLORS.primary }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: COLORS.text }}>
                              Compte Rendu disponible
                            </Typography>
                          </Stack>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleOpenCRModal(compteRendu, res)}
                            sx={{
                              backgroundColor: COLORS.primary,
                              color: '#ffffff',
                              fontWeight: 700,
                              borderRadius: '8px',
                              textTransform: 'none',
                              '&:hover': { backgroundColor: '#3311CC' },
                            }}
                          >
                            Consulter & Imprimer PDF
                          </Button>
                        </Stack>
                      </Box>
                    </>
                  )}
                </Paper>
              );
            })}

            {hasMore && (
              <Box sx={{ textAlign: 'center', pt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setVisibleCount((prev) => prev + 6)}
                  endIcon={<ExpandMoreIcon />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: '10px',
                    borderColor: COLORS.primary,
                    color: COLORS.primary,
                    '&:hover': {
                      backgroundColor: COLORS.primarySoft,
                      borderColor: COLORS.primary,
                    },
                  }}
                >
                  Afficher plus de réservations ({reservations.length - visibleCount} restantes)
                </Button>
              </Box>
            )}
          </Stack>
        )}
      </Paper>

      {/* MODALE D'AFFICHAGE DU COMPTE RENDU ET IMPRESSION PDF */}
      <Dialog open={openCRModal} onClose={handleCloseCRModal} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '20px', p: 2 } }}>
        {selectedCR && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Avatar sx={{ bgcolor: 'rgba(67, 24, 255, 0.1)', color: '#4318FF' }}>
                  <DescriptionIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#2B3674' }}>
                    Compte Rendu - {selectedReservation?.offre?.info_offre?.titre_offre || 'Soin'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#A3AED0' }}>
                    Patient: {patient?.prenom_patient} {patient?.nom_patient}
                  </Typography>
                </Box>
              </Stack>
              <IconButton onClick={handleCloseCRModal} sx={{ color: '#A3AED0' }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <Divider sx={{ my: 1 }} />

            <DialogContent sx={{ mt: 1 }}>
              <Box id="printable-compte-rendu-tab">
                <div className="header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #4318FF', paddingBottom: '10px', marginBottom: '20px' }}>
                  <div>
                    <div className="title" style={{ fontSize: '20px', fontWeight: '800', color: '#4318FF' }}>PLATEFORME S.O.A</div>
                    <div className="subtitle" style={{ fontSize: '16px', fontWeight: 'bold', color: '#2B3674' }}>Compte Rendu & Suivi Médical</div>
                    <div style={{ fontSize: '13px', color: '#718096', marginTop: '4px' }}>
                      Prestation : <strong>{selectedReservation?.offre?.info_offre?.titre_offre || 'N/A'}</strong>
                    </div>
                  </div>
                  <div>
                    <span className="badge" style={{ background: '#E6F9F5', color: '#05CD99', padding: '6px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold', border: '1px solid #B2F1E4' }}>
                      VALIDÉ & SIGNÉ
                    </span>
                  </div>
                </div>

                <Paper className="card" sx={{ p: 2, mb: 3, borderRadius: '16px', bgcolor: '#F4F7FE', boxShadow: 'none' }}>
                  <Grid container spacing={2} className="grid">
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ color: '#2B3674' }}>
                        <strong>Patient :</strong> {patient?.prenom_patient} {patient?.nom_patient}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ color: '#2B3674' }}>
                        <strong>Intervenant/Praticien :</strong> {selectedReservation?.user ? `${selectedReservation.user.prenom_user || ''} ${selectedReservation.user.nom_user || ''}`.trim() : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ color: '#2B3674' }}>
                        <strong>Date de consultation :</strong> {selectedReservation?.start_time_reservation ? new Date(selectedReservation.start_time_reservation).toLocaleString('fr-FR') : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ color: '#2B3674' }}>
                        <strong>Numéro de séance :</strong> Séance n°{selectedCR.numero_seance || selectedCR.id_compte_rendu || 1}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>

                <Paper className="section sec-1" sx={{ p: 2.5, mb: 2, borderRadius: '12px', bgcolor: '#F8FAFC', borderLeft: '4px solid #0284C7', boxShadow: 'none' }}>
                  <Typography variant="subtitle2" className="sec-title" sx={{ fontWeight: 700, color: '#0284C7', mb: 1 }}>
                    <PsychologyIcon fontSize="small" /> Pratique & Approche
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#334155', whiteSpace: 'pre-line' }}>
                    {selectedCR.pratique_approche || selectedCR.pratique || 'Aucune donnée saisie.'}
                  </Typography>
                </Paper>

                <Paper className="section sec-2" sx={{ p: 2.5, mb: 2, borderRadius: '12px', bgcolor: '#F0F9FF', borderLeft: '4px solid #0EA5E9', boxShadow: 'none' }}>
                  <Typography variant="subtitle2" className="sec-title" sx={{ fontWeight: 700, color: '#0EA5E9', mb: 1 }}>
                    <FaceIcon fontSize="small" /> Perception & État du Patient
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#334155', whiteSpace: 'pre-line' }}>
                    {selectedCR.perception_etat_patient || selectedCR.perception_patient || selectedCR.perception || 'Aucune donnée saisie.'}
                  </Typography>
                </Paper>

                <Paper className="section sec-3" sx={{ p: 2.5, mb: 2, borderRadius: '12px', bgcolor: '#F0FDF4', borderLeft: '4px solid #10B981', boxShadow: 'none' }}>
                  <Typography variant="subtitle2" className="sec-title" sx={{ fontWeight: 700, color: '#10B981', mb: 1 }}>
                    <MedicalServicesIcon fontSize="small" /> Résumé & Actes Réalisés
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#334155', whiteSpace: 'pre-line' }}>
                    {selectedCR.resume_actes_realises || selectedCR.resume_seance || selectedCR.resume || 'Aucune donnée saisie.'}
                  </Typography>
                </Paper>

                <Paper className="section sec-4" sx={{ p: 2.5, borderRadius: '12px', bgcolor: '#EEF2FF', borderLeft: '4px solid #6366F1', boxShadow: 'none' }}>
                  <Typography variant="subtitle2" className="sec-title" sx={{ fontWeight: 700, color: '#6366F1', mb: 1 }}>
                    <CalendarTodayIcon fontSize="small" /> Plan de Soins & Suite
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#334155', whiteSpace: 'pre-line' }}>
                    {selectedCR.plan_de_soins || selectedCR.plan || 'Aucune donnée saisie.'}
                  </Typography>
                </Paper>
              </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
              <Button onClick={handleCloseCRModal} sx={{ color: '#A3AED0', fontWeight: 600 }}>
                Fermer
              </Button>
              <Button
                variant="contained"
                startIcon={<PrintIcon />}
                onClick={handlePrintPDF}
                sx={{
                  bgcolor: '#4318FF',
                  borderRadius: '12px',
                  px: 3,
                  textTransform: 'none',
                  fontWeight: 700,
                  '&:hover': { bgcolor: '#3311CC' },
                }}
              >
                Télécharger en PDF / Imprimer
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}